import React, { useState, useEffect, useMemo } from "react";
import { AgChartsReact } from "ag-charts-react";
import { Box, FormControl, Select, MenuItem, Typography, Stack, Skeleton, Checkbox, ListItemText } from "@mui/material";
import { styled } from "@mui/material/styles";
import ApiService from "../../../utils/fetch";

// Pill styles
const PillContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  borderRadius: 20,
  background: '#f5f5f5',
  height: 32,
  minWidth: 180,
  alignItems: 'center',
  boxShadow: '0 0 0 1px #ddd',
}));

const PillHighlight = styled('div')(({ selected, count }) => ({
  position: 'absolute',
  top: 2,
  left: selected * (100 / count) + "%",
  width: `calc(${100 / count}% - 4px)`,
  height: 'calc(100% - 4px)',
  background: '#222',
  borderRadius: 16,
  transition: 'left 0.25s cubic-bezier(.4,0,.2,1)',
  zIndex: 1,
}));

const PillLabel = styled('div')(({ active }) => ({
  position: 'relative',
  zIndex: 2,
  flex: 1,
  textAlign: 'center',
  fontSize: '0.85rem',
  fontWeight: active ? 500 : 400,
  color: active ? '#fff' : '#222',
  cursor: 'pointer',
  padding: '0 8px',
  userSelect: 'none',
  transition: 'color 0.2s',
  lineHeight: '28px',
}));

const aggOptions = [
  { value: "plant", label: "Plant" },
  { value: "bottler", label: "Bottler" },
  { value: "country", label: "Country" }
];

const termOptions = [
  { value: "1", label: "Short Term" },
  { value: "2", label: "Long Term" }
];

const metricOptions = [
  { value: "success", label: "Success %" },
  { value: "onTime", label: "On Time %" },
  { value: "variance", label: "Variance %" },
  { value: "total", label: "Submissions" }
];

const timeOptions = [
  { value: "last12", label: "Past Year" },
  { value: "last6", label: "Past 6 Months" },
  { value: "last3", label: "Past 3 Months" },
  { value: "current", label: "Current Month" }
];

const PerformanceGraph = () => {
  // State for selectors
  const [timePeriod, setTimePeriod] = useState("last3"); // Default to "Past 3 Months"
  const [term, setTerm] = useState("1");
  const [agg, setAgg] = useState("plant");
  const [metric, setMetric] = useState("success");

  const [monthList, setMonthList] = useState([]);
  const [monthListLoading, setMonthListLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  // New: Track all available entities for filtering
  const [allEntities, setAllEntities] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);

  // Fetch fiscal months from API and sync with dropdown
  useEffect(() => {
    setMonthListLoading(true);
    ApiService.get(`/v1/metrics/getPastNMonths/12`)
      .then(response => {
        const months = response.data?.months || [];
        setMonthList(months);
        // Default to last3 (past 3 months) on first load
        setSelectedMonths(months.slice(0, 3));
      })
      .catch(() => {
        setMonthList([]);
        setSelectedMonths([]);
      })
      .finally(() => setMonthListLoading(false));
  }, []);

  // Update selectedMonths when timePeriod or monthList changes
  useEffect(() => {
    if (!monthList || monthList.length === 0) return;
    let count = 12; // Default to 12 months ("last12")
    if (timePeriod === "last6") count = 6;
    else if (timePeriod === "last9") count = 9;
    else if (timePeriod === "last3") count = 3;
    else if (timePeriod === "current") count = 1;

    if (timePeriod === "current") {
      // Only show the latest month
      setSelectedMonths([monthList[0]]);
    } else {
      // Exclude the latest month (monthList[0]) for all other periods
      setSelectedMonths(monthList.slice(1, count + 1));
    }
  }, [timePeriod, monthList]);

  // Fetch chart data based on all filters
  useEffect(() => {
    if (!selectedMonths.length) {
      setChartData([]);
      return;
    }
    setChartLoading(true);

    if (metric === "success" || metric === "total") {
      ApiService.post('/v1/metrics/get-forecast-percantage-metrics', {
        aggregateLevel: agg,
        fiscalMonths: selectedMonths,
        forecastTerm: Number(term)
      })
        .then(res => {
          let data = Array.isArray(res.data) ? res.data : res.data?.metrics;
          setChartData(Array.isArray(data) ? data : []);
        })
        .catch(() => setChartData([]))
        .finally(() => setChartLoading(false));
    } else if (metric === "onTime" || metric === "variance") {
      ApiService.post('/v1/metrics/get-forecast-performance-details', {
        aggregateLevel: agg,
        monthList: selectedMonths,
        forecastTerm: Number(term)
      })
        .then(res => {
          setChartData(Array.isArray(res.data) ? res.data : []);
        })
        .catch(() => setChartData([]))
        .finally(() => setChartLoading(false));
    }
  }, [selectedMonths, term, agg, metric]);

  // Prepare AG Line Chart options based on metric and agg
  const getLineChartOptions = () => {
    if (!chartData || chartData.length === 0 || !selectedMonths.length) {
      return {
        data: [],
        series: [],
        axes: [],
        legend: { position: "top" },
        height: 320,
      };
    }

    // X axis: months
    const months = selectedMonths.slice().reverse();

    // Group data by entity (plant, bottler, or country)
    let groupKey, groupLabel;
    if (agg === "plant") {
      groupKey = "plantId" in chartData[0] ? "plantId" : "locationId";
      groupLabel = "plantDesc" in chartData[0] ? "plantDesc" : "locationDescription";
    } else if (agg === "bottler") {
      groupKey = "bottler";
      groupLabel = "bottler";
    } else {
      groupKey = "country" in chartData[0] ? "country" : "customerCountryDesc";
      groupLabel = "country" in chartData[0] ? "country" : "customerCountryDesc";
    }

    // Build series for each entity
    const entityMap = {};
    chartData.forEach(item => {
      const key = item[groupKey] || item[groupLabel] || "";
      if (!entityMap[key]) entityMap[key] = { label: item[groupLabel] || key, data: {} };
      entityMap[key].data[item.fiscalMonth] = item;
    });

    // Prepare AG Charts series
    const series = [];
    Object.values(entityMap).forEach((entity, idx) => {
      const dataPoints = months.map(month => {
        const d = entity.data[month] || {};
        let yVal = 0;
        if (metric === "success") yVal = d.successPercent ?? d.success_percentage ?? 0;
        else if (metric === "onTime") yVal = d.ontimePercent ?? d.onTimePercent ?? 0;
        else if (metric === "variance") yVal = d.variancePercent  ?? 0;
        else if (metric === "total") yVal = d.totalCount ?? d.total_submissions ?? 0;
        return { month, value: yVal };
      });

      // For plant/location, legend as plantCode - plantName
      let legendLabel = entity.label;
      if (agg === "plant" && chartData[0]) {
        // Try to get plantId and plantDesc or locationId and locationDescription
        const sample = Object.values(entity.data)[0] || {};
        const code = sample.plantId || sample.locationId || "";
        const name = sample.plantDesc || sample.locationDescription || "";
        legendLabel = code && name ? `${code} - ${name}` : entity.label;
      }

      // Extended color palette with 20 distinct colors
      // const colorPalette = [
      //   "#1976d2", "#F79900", "#90EE90", "#FF0000", "#8e24aa", "#43a047", "#339800ff", "#A3C1DA", "#FFD700", "#00BFFF",
      //   "#FF69B4", "#8B0000", "#00FF7F", "#FF7F50", "#6A5ACD", "#20B2AA", "#B8860B", "#080707ff", "#7FFF00", "#df94ceff"
      // ];
      const colorPalette = [
  "#1976D2", // Blue
  "#F49900", // Vibrant Orange
  "#90EE90", // Light Green
  "#FF0000", // Red
  "#8E24AA", // Purple
  "#43A047", // Dark Green
  "#3398FF", // Bright Sky Blue
  "#A3B8DA", // Soft Blue
  "#FFD700", // Gold
  "#00BFFF", // Deep Sky Blue
  "#FF69B4", // Pink
  "#8B0000", // Dark Red
  "#00FF7F", // Spring Green
  "#FF7F50", // Coral
  "#6A5ACD", // Slate Blue
  "#20B2AA", // Light Sea Green
  "#B8860B", // Dark Goldenrod
  "#080808", // Almost Black
  "#7FFF00", // Chartreuse Green
  "#DF94CE"  // Light Pink Lavender
];
      const color = colorPalette[idx % colorPalette.length];

      series.push({
        type: "line",
        xKey: "month",
        yKey: "value",
        data: dataPoints,
        title: legendLabel,
        stroke: color,
        marker: { shape: "circle", size: 10, fill: color, stroke: color },
        label: { enabled: false }
      });
    });

    return {
      data: months.map(month => ({ month })), // just for x axis
      series,
      axes: [
        { type: "category", position: "bottom", title: { text: "Month" } },
        { type: "number", position: "left", title: { text: metric === "total" ? "Submissions" : "%" } }
      ],
      legend: { position: "top", preventHidingAll: true },
      height: 320,
    };
  };

  // Update allEntities and selectedEntities when chartData or agg/metric changes
  useEffect(() => {
    if (!chartData || chartData.length === 0 || !selectedMonths.length) {
      setAllEntities([]);
      setSelectedEntities([]);
      return;
    }
    // Determine groupKey and groupLabel as in getLineChartOptions
    let groupKey, groupLabel;
    if (agg === "plant") {
      groupKey = "plantId" in chartData[0] ? "plantId" : "locationId";
      groupLabel = "plantDesc" in chartData[0] ? "plantDesc" : "locationDescription";
    } else if (agg === "bottler") {
      groupKey = "bottler";
      groupLabel = "bottler";
    } else {
      groupKey = "country" in chartData[0] ? "country" : "customerCountryDesc";
      groupLabel = "country" in chartData[0] ? "country" : "customerCountryDesc";
    }
    // Build unique entity keys and labels
    const entityMap = {};
    chartData.forEach(item => {
      const key = item[groupKey] || item[groupLabel] || "";
      let label = item[groupLabel] || key;
      if (agg === "plant") {
        const code = item.plantId || item.locationId || "";
        const name = item.plantDesc || item.locationDescription || "";
        label = code && name ? `${code} - ${name}` : label;
      }
      entityMap[key] = label;
    });
    const entityList = Object.entries(entityMap).map(([key, label]) => ({ key, label }));
    setAllEntities(entityList);
    setSelectedEntities(entityList.map(e => e.key)); // Default: all selected
  }, [chartData, agg, metric, selectedMonths]);

  // Handler for dropdown
  const handleEntityFilterChange = (event) => {
    const value = event.target.value;
    setSelectedEntities(typeof value === "string" ? value.split(",") : value);
  };

  // Memoize filtered series for chart
  const filteredAgChartOptions = useMemo(() => {
    const baseOptions = getLineChartOptions();
    if (!baseOptions.series || !Array.isArray(baseOptions.series)) return baseOptions;
    // Filter series by selectedEntities
    const filteredSeries = baseOptions.series.filter((s, idx) => {
      // Find the entity key for this series
      // We need to match the same key as in allEntities
      // Use the title as label, and find the corresponding key
      const entity = allEntities.find(e => e.label === s.title);
      return entity && selectedEntities.includes(entity.key);
    });
    return { ...baseOptions, series: filteredSeries };
  }, [getLineChartOptions, allEntities, selectedEntities]);

  const agChartOptions = getLineChartOptions();

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        {/* 1. Time period dropdown */}
        <FormControl
          size="small"
          sx={{
            border: "1px solid black",
            borderRadius: 5,
            px: 0.25,
            minWidth: 120,
            height: 28,
            backgroundColor: "#fff",
          }}
        >
          {monthListLoading ? (
            <Skeleton variant="rectangular" width={120} height={28} />
          ) : (
            <Select
              value={timePeriod}
              onChange={e => setTimePeriod(e.target.value)}
                MenuProps={{
                  disableScrollLock: true, // Disable scroll lock for the Select's dropdown menu
                }}
              sx={{
                fontSize: "0.8rem",
                padding: "6px 8px",
                ".MuiOutlinedInput-input": {
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                },
                ".MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              {timeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          )}
        </FormControl>
        {/* 2. Term pill selector */}
        <PillContainer sx={{ minWidth: 180 }}>
          <PillHighlight selected={term === "1" ? 0 : 1} count={2} />
          {termOptions.map((opt, idx) => (
            <PillLabel
              key={opt.value}
              active={term === opt.value}
              onClick={() => setTerm(opt.value)}
            >
              {opt.label}
            </PillLabel>
          ))}
        </PillContainer>
        {/* 3. Aggregate pill selector */}
        <PillContainer sx={{ minWidth: 300 }}>
          <PillHighlight selected={aggOptions.findIndex(o => o.value === agg)} count={aggOptions.length} />
          {aggOptions.map((opt, idx) => (
            <PillLabel
              key={opt.value}
              active={agg === opt.value}
              onClick={() => setAgg(opt.value)}
            >
              {opt.label}
            </PillLabel>
          ))}
        </PillContainer>
        {/* 4. Metric pill selector */}
        <PillContainer sx={{ minWidth: 400 }}>
          <PillHighlight selected={metricOptions.findIndex(o => o.value === metric)} count={metricOptions.length} />
          {metricOptions.map((opt, idx) => (
            <PillLabel
              key={opt.value}
              active={metric === opt.value}
              onClick={() => setMetric(opt.value)}
            >
              {opt.label}
            </PillLabel>
          ))}
        </PillContainer>
      </Stack>
      {/* 5. Custom entity filter dropdown on a new line */}
      {allEntities.length > 1 && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center" }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 200,
              maxWidth: 200,
              border: "1px solid #bbb",
              borderRadius: 2,
              background: "#fff",
            }}
          >
            <Select
              multiple
              value={selectedEntities}
              onChange={handleEntityFilterChange}
              renderValue={selected => {
                if (selected.length === allEntities.length) return "All";
                return allEntities
                  .filter(e => selected.includes(e.key))
                  .map(e => e.label)
                  .join(", ");
              }}
              sx={{
                fontSize: "0.8rem",
                ".MuiOutlinedInput-notchedOutline": { border: "none" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                py: 0.2,
                minHeight: 28,
                height: 28,
              }}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 320, width: 180, fontSize: "0.8rem" },
                 
                },
                 disableScrollLock: true 
              }}
            >
              {allEntities.map(entity => (
                <MenuItem
                  key={entity.key}
                  value={entity.key}
                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 28,
                    height: 28,
                    py: 0,
                  }}
                >
                  <Checkbox
                    checked={selectedEntities.indexOf(entity.key) > -1}
                    size="small"
                    sx={{ p: 0.5 }}
                  />
                  <ListItemText
                    primary={entity.label}
                    primaryTypographyProps={{ fontSize: "0.8rem" }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {/* Chart */}
      <div className="container" style={{height: 320 }}>
        {chartLoading ? (
          <Skeleton variant="rectangular" width="100%" height={320} />
        ) : (
          <AgChartsReact options={filteredAgChartOptions} />
        )}
      </div>
    </Box>
  );
};

export default PerformanceGraph;